import { useEffect } from 'react';
import styles from './styles.module.css';

import buzzyAward from '../../public/static/images/shopping-stickers/buzzy-award.png';
import copycats from '../../public/static/images/shopping-stickers/copycats.png';
import diamondGemstone from '../../public/static/images/shopping-stickers/diamond-gemstone-1.png';
import flexingArm from '../../public/static/images/shopping-stickers/strong-arm-emoji-flexing-1.png';
import frogLove from '../../public/static/images/shopping-stickers/frog-love.png';
import frogTrending from '../../public/static/images/shopping-stickers/frog-trending-arrows.png';
import frogDollar from '../../public/static/images/shopping-stickers/frog-dollar-sign.png';
import lightBulb from '../../public/static/images/shopping-stickers/light-bulb.png';
import mindBlown from '../../public/static/images/shopping-stickers/mind-blown.png';
import trophy from '../../public/static/images/shopping-stickers/trophy.png';
import wand from '../../public/static/images/shopping-stickers/wand.png';

const TAG_TO_STICKER = {
  'Buzzy Award': buzzyAward,
  'Copycats': copycats,
  'Diamond gemstone': diamondGemstone,
  'Flexing arm emoji': flexingArm,
  'Frog - love': frogLove,
  'Frog - trending arrow eyes': frogTrending,
  'Frog - dollar sign eyes': frogDollar,
  'Lightbulb': lightBulb,
  'Mind-blown': mindBlown,
  'Trophy': trophy,
  'Wand': wand,
};

const normalizeTag = (tag = '') =>
  tag.toLowerCase().replace(/[–—-]/g, ' ').replace(/\s+/g, ' ').trim();

const normalizedMap = Object.fromEntries(
  Object.entries(TAG_TO_STICKER).map(([k, v]) => [normalizeTag(k), v])
);

/**
 * Build a map of subbuzzId -> stickerSrc from buzz.sub_buzzes.
 * Each product subbuzz has an id that matches the `subbuzz-anchor` div id
 * already rendered into the HTML (e.g. <div id="buzz_sub_buzz_12345">).
 */
const buildStickerIndex = (buzz) => {
  const index = {};
  (buzz?.sub_buzzes || []).forEach((sb) => {
    if (sb.format_name !== 'product_subbuzz') return;
    const tags = sb.bfp_data?.data?.other_tags;
    if (!Array.isArray(tags) || tags.length === 0) return;
    const matchedKey = tags.map(normalizeTag).find((t) => normalizedMap[t]);
    if (matchedKey) {
      index[sb.id] = normalizedMap[matchedKey];
    }
  });
  return index;
};

export function useShoppingStickers(containerRef, isEnabled, buzz) {
  useEffect(() => {
    if (!isEnabled) return undefined;
    const container = containerRef.current;
    if (!container) return undefined;

    // Build id->sticker lookup once from buzz data — no data-other-tags needed in HTML.
    const stickerIndex = buildStickerIndex(buzz);
    if (Object.keys(stickerIndex).length === 0) return undefined;

    const sync = () => {
      // Each rendered product subbuzz contains a .subbuzz-anchor whose id
      // is the subbuzz id (e.g. "buzz_sub_buzz_12345"). Walk up to the
      // js-subbuzz-wrapper to find the right scoping element.
      Object.entries(stickerIndex).forEach(([subbuzzId, stickerSrc]) => {
        try {
          const anchor = container.querySelector(`#${CSS.escape(subbuzzId)}`);
          if (!anchor) return;

          const wrapper = anchor.closest('.js-subbuzz-wrapper') || anchor.parentElement;

          const existingImg = wrapper.querySelector(`.${styles.shoppingSticker_img}`);

          // Sticker already exists — update src if it changed
          if (existingImg) {
            if (existingImg.getAttribute('src') !== stickerSrc) {
              existingImg.setAttribute('src', stickerSrc);
            }
            return;
          }

          // Target the images container
          const imgContainer = wrapper.querySelector('[data-shopping-images-container]')
            || wrapper.querySelector('[class*="images_container"]')
            || wrapper.querySelector('[class*="image__media-container"]')
            || wrapper;

          if (!imgContainer.classList.contains(styles.shoppingSticker_wrapper)) {
            imgContainer.classList.add(styles.shoppingSticker_wrapper);
          }

          const img = document.createElement('img');
          img.src = stickerSrc;
          img.alt = '';
          img.setAttribute('aria-hidden', 'true');
          img.setAttribute('draggable', 'false');
          img.className = styles.shoppingSticker_img;

          imgContainer.appendChild(img);
        } catch (e) {
          // Silently ignore
        }
      });
    };

    sync();

    return () => {
      container
        .querySelectorAll(`.${styles.shoppingSticker_img}`)
        .forEach((img) => img.remove());
      container
        .querySelectorAll(`.${styles.shoppingSticker_wrapper}`)
        .forEach((el) => el.classList.remove(styles.shoppingSticker_wrapper));
    };
  }, [containerRef, isEnabled, buzz]);
}
